import React, { useState, useEffect } from 'react';
import { Settings, Save, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputStyle = "rounded-xl border border-zinc-700 bg-black/40 text-zinc-100 placeholder:text-zinc-500 outline-none focus-visible:ring-0 px-4 py-2.5";

const TimeRulesSettings = ({ locationId, sessionId }) => {
  const [rules, setRules] = useState({
    allow_early_checkin_minutes: 0,
    allow_late_checkout_minutes: 0,
    require_overtime_request_for_early_checkin: true,
    block_checkout_before_schedule: true,
    show_late_early_alerts: true,
    notify_manager_of_rule_breaks: true
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadTimeRules();
  }, [locationId]);

  const loadTimeRules = async () => {
    try {
      const response = await axios.get(
        `${API}/manager/location/${locationId}/time-rules`,
        { params: { session_id: sessionId } }
      );
      if (response.data.success) {
        setRules(response.data.time_rules);
      }
    } catch (error) {
      console.error('Error loading time rules:', error);
      // Silently fail - time rules may not be available for all plans/locations
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API}/manager/location/${locationId}/time-rules`,
        rules,
        { params: { session_id: sessionId } }
      );

      if (response.data.success) {
        toast.success('Time rules updated successfully');
      }
    } catch (error) {
      console.error('Error updating time rules:', error);
      toast.error('Failed to update time rules');
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save a single toggle setting
  const handleToggleChange = async (key, value) => {
    const newRules = { ...rules, [key]: value };
    setRules(newRules);
    
    try {
      await axios.put(
        `${API}/manager/location/${locationId}/time-rules`,
        newRules,
        { params: { session_id: sessionId } }
      );
    } catch (error) {
      console.error('Error saving toggle:', error);
      toast.error('Failed to save setting');
      setRules(rules);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Windows */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Early Check-in Window */}
        <div className="rounded-2xl bg-zinc-900/80 border border-zinc-800 p-6">
          <div className="text-sm font-semibold text-white mb-2">Early Check-in Window</div>
          <div className="text-xs text-zinc-400 mb-4">
            Minutes before shift start employees are allowed to check in
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              max="120"
              value={rules.allow_early_checkin_minutes}
              onChange={(e) => setRules({...rules, allow_early_checkin_minutes: parseInt(e.target.value) || 0})}
              className={`w-24 ${inputStyle}`}
            />
            <span className="text-sm text-zinc-400">minutes</span>
          </div>
        </div>

        {/* Late Check-out Window */}
        <div className="rounded-2xl bg-zinc-900/80 border border-zinc-800 p-6">
          <div className="text-sm font-semibold text-white mb-2">Late Check-out Window</div>
          <div className="text-xs text-zinc-400 mb-4">
            Minutes after shift end employees are allowed to check out
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              max="120"
              value={rules.allow_late_checkout_minutes}
              onChange={(e) => setRules({...rules, allow_late_checkout_minutes: parseInt(e.target.value) || 0})}
              className={`w-24 ${inputStyle}`}
            />
            <span className="text-sm text-zinc-400">minutes</span>
          </div>
        </div>
      </div>

      {/* Toggles Section */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 divide-y divide-zinc-800">
        {/* Require Overtime Request */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="min-w-0 pr-4">
            <div className="text-sm font-medium text-white">Require Overtime Request for Early Check-in</div>
            <div className="mt-1 text-xs text-zinc-400">Staff must request approval before checking in early</div>
          </div>
          <Switch
            checked={rules.require_overtime_request_for_early_checkin}
            onCheckedChange={(checked) => handleToggleChange('require_overtime_request_for_early_checkin', checked)}
          />
        </div>

        {/* Block Early Checkout */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="min-w-0 pr-4">
            <div className="text-sm font-medium text-white">Block Check-out Before Scheduled End</div>
            <div className="mt-1 text-xs text-zinc-400">Prevent early check-outs unless permitted</div>
          </div>
          <Switch
            checked={rules.block_checkout_before_schedule}
            onCheckedChange={(checked) => handleToggleChange('block_checkout_before_schedule', checked)}
          />
        </div>

        {/* Show Late/Early Alerts */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="min-w-0 pr-4">
            <div className="text-sm font-medium text-white">Show Late/Early Alerts on Tablet</div>
            <div className="mt-1 text-xs text-zinc-400">Display visual alerts on the check-in device</div>
          </div>
          <Switch
            checked={rules.show_late_early_alerts}
            onCheckedChange={(checked) => handleToggleChange('show_late_early_alerts', checked)}
          />
        </div>

        {/* Notify Manager */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="min-w-0 pr-4">
            <div className="text-sm font-medium text-white">Notify Manager of Rule Breaks</div>
            <div className="mt-1 text-xs text-zinc-400">Send dashboard notifications when rules are violated</div>
          </div>
          <Switch
            checked={rules.notify_manager_of_rule_breaks}
            onCheckedChange={(checked) => handleToggleChange('notify_manager_of_rule_breaks', checked)}
          />
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={isSaving}
        className="w-full bg-gradient-to-r from-emerald-500/20 to-violet-500/20 hover:from-emerald-500/30 hover:to-violet-500/30 disabled:opacity-50 text-white font-medium py-3 px-4 rounded-xl transition-all border border-zinc-700 flex items-center justify-center gap-2"
      >
        <Save className="w-4 h-4" />
        {isSaving ? 'Saving...' : 'Save Time Rules'}
      </button>
    </div>
  );
};

export default TimeRulesSettings;
